/**
 * Method-Based Assets Module - PSP/CRE Tool v3.0
 * 
 * @module methodBasedAssetsModule
 * @version 2.1.0
 * @date February 24, 2026
 * @author Dr. Tatar Radu MD / Claude AI
 *
 * VERSION 2.1.0 - Phase 3-C MAM-03/04 constants migration (February 24, 2026)
 * - MAM-03: Deleted local `AUTO_THRESHOLDS` — replaced with `AUTO_METHOD_THRESHOLDS`
 *   from evacuationAssetFactors.js. Values identical. 4 usage sites updated.
 * - MAM-04: Deleted local `PUSH_DISTANCE_FACTORS` — replaced with `PUSH_DISTANCE_RATIOS`
 *   from evacuationAssetFactors.js. Values identical. getPushRatioByDistance() updated.
 *   Re-exported as PUSH_DISTANCE_FACTORS alias for backward compatibility.
 * STILL DEFERRED: MAM-02 (EVACUATION_METHODS — no canonical equivalent),
 *   MAM-05 (ASSET_TYPES — key mismatch), MAM-06 (TIME_IMPACT — no canonical equivalent).
 *
 * VERSION 2.0.0 - Phase 3-C partial constants migration (February 22, 2026)
 * - MAM-07: calculateEvacuationCapacity() — replaced `avgSpeed = 30` literal
 *   with `CONVOY_TACTICAL_SPEED_KMH` imported from ambulanceTypes.js (EXT-03).
 * - MAM-08: Removed `|| 20` silent default distance in calculateMethodBasedAssets()
 *   and calculateAllFacilityAssets(). Missing distance now throws with context
 *   (APP_RULES 6.3: no silent defaults that mask configuration gaps).
 *
 * DEFERRED items (canonical source files not yet available):
 * - MAM-01: ✅ COMPLETE — all 3 imports done (CONVOY_TACTICAL_SPEED_KMH, AUTO_METHOD_THRESHOLDS, PUSH_DISTANCE_RATIOS).
 * - MAM-02: PERMANENTLY DEFERRED — no canonical equivalent (see module comment).
 * - MAM-01/03: AUTO_THRESHOLDS — evacuationAssetFactors.js does not exist yet.
 * - MAM-01/04: PUSH_DISTANCE_FACTORS — evacuationAssetFactors.js does not exist yet.
 * - MAM-01/05: ASSET_TYPES — ambulanceTypes.js has vehicle specs but key mapping
 *   differs; deferred to Phase 4+ restructure.
 * - MAM-06: PERMANENTLY DEFERRED — no canonical equivalent (see module comment).
 * NOTE: calculatePushDOWImpact() uses `* 30` and `> 30/60` as delay-minutes
 *   estimates, NOT convoy speed — intentionally left as-is.
 *
 * @description
 * Calculates effective evacuation asset allocation based on PUSH/PULL method.
 * PUSH method splits assets between collection and forward delivery.
 * PULL method maintains 100% collection capacity.
 * AUTO mode dynamically decides based on capacity and load.
 * 
 * Issue ID: CC-006
 * Priority: HIGH
 * 
 * Doctrinal Reference:
 * - NATO AJMedP-2: Allied Joint Medical Evacuation Doctrine
 * - US Army ATP 4-02.2: Medical Evacuation
 * - Ukraine 2022-2024: Observed PUSH usage during MASCAL events
 */

// ============================================================================
// IMPORTS
// MAM-07: CONVOY_TACTICAL_SPEED_KMH from ambulanceTypes.js (EXT-03).
// MAM-03: AUTO_METHOD_THRESHOLDS from evacuationAssetFactors.js (replaces local AUTO_THRESHOLDS).
// MAM-04: PUSH_DISTANCE_RATIOS from evacuationAssetFactors.js (replaces local PUSH_DISTANCE_FACTORS).
// MAM-02/06 permanently deferred — EVACUATION_METHODS (PULL/PUSH/AUTO) and TIME_IMPACT
// have no canonical equivalent in evacuationParadigms.js. EVACUATION_TYPES there is
// MEDEVAC/CASEVAC/SELF_AID (platform type), a completely different concept.
// ============================================================================

import { CONVOY_TACTICAL_SPEED_KMH } from '../../constants/ambulanceTypes';
import {
  AUTO_METHOD_THRESHOLDS,
  PUSH_DISTANCE_RATIOS
} from '../../constants/evacuationAssetFactors';

// ============================================================================
// CONSTANTS
// ============================================================================

/**
 * Evacuation methods
 * MAM-02 PERMANENTLY DEFERRED: evacuationParadigms.js EVACUATION_TYPES is MEDEVAC/CASEVAC/SELF_AID
 * (platform type + medical capability). Local EVACUATION_METHODS is PULL/PUSH/AUTO
 * (logistics flow direction). Different concepts — no canonical equivalent exists.
 */
export const EVACUATION_METHODS = {
  PULL: {
    code: 'PULL',
    name: 'Pull Evacuation',
    description: 'Receiving facility collects casualties from sending facility',
    collection_modifier: 1.0,  // 100% collection capacity
    forward_modifier: 0.0,     // No forward push
    when_to_use: 'Normal operations, adequate receiving capacity'
  },
  PUSH: {
    code: 'PUSH',
    name: 'Push Evacuation',
    description: 'Sending facility pushes casualties forward to receiving facility',
    collection_modifier: 0.5,  // Split - 50% for collection
    forward_modifier: 0.5,     // Split - 50% for forward push
    when_to_use: 'MASCAL, receiving facility overwhelmed, forward movement needed'
  },
  AUTO: {
    code: 'AUTO',
    name: 'Automatic Method Selection',
    description: 'System determines optimal method based on capacity and load',
    collection_modifier: null, // Calculated dynamically
    forward_modifier: null,    // Calculated dynamically
    when_to_use: 'Uncertain situation, allow system to optimize'
  }
};

/**
 * AUTO method decision thresholds
 */
/**
 * MAM-03 FIX: Local AUTO_THRESHOLDS deleted.
 * Imported as AUTO_METHOD_THRESHOLDS from evacuationAssetFactors.js (identical values).
 * Re-exported as AUTO_THRESHOLDS for backward compatibility.
 */
export { AUTO_METHOD_THRESHOLDS as AUTO_THRESHOLDS };

/**
 * PUSH ratio calculation factors based on distance
 * Longer distance = more assets needed for forward delivery
 * MAM-04 FIX: Local PUSH_DISTANCE_FACTORS deleted.
 * Imported as PUSH_DISTANCE_RATIOS from evacuationAssetFactors.js (identical values).
 * Re-exported as PUSH_DISTANCE_FACTORS for backward compatibility.
 */
export { PUSH_DISTANCE_RATIOS as PUSH_DISTANCE_FACTORS };

/**
 * Asset types for allocation
 * MAM-05 DEFERRED: Replace with ambulanceTypes.js vehicle specs when key mapping
 * between local GROUND_AMBULANCE/APC_MEDICAL and ambulanceTypes.js is resolved.
 */
export const ASSET_TYPES = {
  GROUND_AMBULANCE: {
    code: 'GROUND',
    patients_per_trip: 4,
    round_trip_factor: 2.5,  // Time multiplier for round trip vs one-way
    can_push: true,
    can_pull: true
  },
  APC_MEDICAL: {
    code: 'APC',
    patients_per_trip: 6,
    round_trip_factor: 2.0,
    can_push: true,
    can_pull: true
  },
  MEDEVAC_HELICOPTER: {
    code: 'MEDEVAC',
    patients_per_trip: 6,
    round_trip_factor: 1.5,  // Faster turnaround
    can_push: false,         // MEDEVAC typically does dedicated missions
    can_pull: true
  },
  CASEVAC: {
    code: 'CASEVAC',
    patients_per_trip: 2,
    round_trip_factor: 3.0,  // Non-dedicated, slower
    can_push: true,
    can_pull: true
  }
};

/**
 * Time impact modifiers for PUSH vs PULL
 * MAM-06 PERMANENTLY DEFERRED: evacuationParadigms.js has no PULL/PUSH timing modifiers.
 * TIME_IMPACT (collection_time_modifier, forward_delivery_modifier) is specific to
 * logistics flow direction — no canonical equivalent exists.
 */
export const TIME_IMPACT = {
  PULL: {
    collection_time_modifier: 1.0,    // Normal collection time
    forward_delivery_modifier: 1.0,   // Normal delivery time
    waiting_time_reduction: 0         // No reduction
  },
  PUSH: {
    collection_time_modifier: 1.3,    // 30% longer collection (fewer assets)
    forward_delivery_modifier: 0.8,   // 20% faster delivery (dedicated assets)
    waiting_time_reduction: 0.25      // 25% less waiting at receiving
  }
};

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Get PUSH ratio based on distance
 * @param {number} distanceKm - Distance to receiving facility
 * @returns {Object} Collection and forward ratios
 */
export const getPushRatioByDistance = (distanceKm) => {
  if (distanceKm <= PUSH_DISTANCE_RATIOS.SHORT.max_km) {
    return PUSH_DISTANCE_RATIOS.SHORT;
  }
  if (distanceKm <= PUSH_DISTANCE_RATIOS.MEDIUM.max_km) {
    return PUSH_DISTANCE_RATIOS.MEDIUM;
  }
  if (distanceKm <= PUSH_DISTANCE_RATIOS.LONG.max_km) {
    return PUSH_DISTANCE_RATIOS.LONG;
  }
  return PUSH_DISTANCE_RATIOS.EXTENDED;
};

/**
 * Calculate total ground ambulances from organic assets
 * @param {Object} organicAssets - Facility organic assets
 * @returns {number} Total ground ambulances
 */
export const getTotalGroundAmbulances = (organicAssets) => {
  if (!organicAssets) return 0;
  
  let total = 0;
  
  // Handle different asset structure formats
  if (organicAssets.ground_ambulances) {
    total = organicAssets.ground_ambulances;
  } else if (organicAssets.ambulances) {
    // May be array or object
    if (Array.isArray(organicAssets.ambulances)) {
      total = organicAssets.ambulances.reduce((sum, amb) => sum + (amb.quantity || 1), 0);
    } else if (typeof organicAssets.ambulances === 'object') {
      total = Object.values(organicAssets.ambulances).reduce((sum, qty) => sum + qty, 0);
    } else {
      total = organicAssets.ambulances;
    }
  } else if (organicAssets.m997) {
    total = organicAssets.m997 + (organicAssets.m113_ambulance || 0);
  }
  
  return total;
};

/**
 * Calculate evacuation capacity (patients per hour)
 * @param {number} ambulances - Number of ambulances
 * @param {number} distanceKm - One-way distance in km
 * @param {number} patientsPerTrip - Patients per ambulance trip
 * @returns {number} Patients per hour capacity
 */
export const calculateEvacuationCapacity = (ambulances, distanceKm, patientsPerTrip = 4) => {
  if (ambulances <= 0 || distanceKm <= 0) return 0;
  
  // MAM-07: CONVOY_TACTICAL_SPEED_KMH replaces literal 30 (APP_RULES 2.1 single source of truth)
  const avgSpeed = CONVOY_TACTICAL_SPEED_KMH;
  const oneWayTime = distanceKm / avgSpeed; // hours
  const roundTripTime = oneWayTime * 2.5;   // Include loading/unloading
  const tripsPerHour = 1 / roundTripTime;
  
  return Math.floor(ambulances * tripsPerHour * patientsPerTrip);
};

/**
 * Determine if AUTO should select PUSH based on conditions
 * @param {Object} conditions - Current operational conditions
 * @returns {Object} Decision result
 */
export const shouldAutoPush = (conditions) => {
  const {
    receiving_load_percent = 0,
    collection_queue = 0,
    casualty_rate_per_hour = 0,
    distance_km = 20
  } = conditions;
  
  const reasons = [];
  let score = 0;
  
  // Check receiving facility overload
  if (receiving_load_percent >= AUTO_METHOD_THRESHOLDS.RECEIVING_OVERLOAD_PERCENT) {
    score += 2;
    reasons.push(`Receiving facility at ${receiving_load_percent}% capacity`);
  }
  
  // Check collection queue
  if (collection_queue >= AUTO_METHOD_THRESHOLDS.COLLECTION_QUEUE_THRESHOLD) {
    score += 2;
    reasons.push(`Collection queue of ${collection_queue} casualties`);
  }
  
  // Check casualty rate
  if (casualty_rate_per_hour >= AUTO_METHOD_THRESHOLDS.MIN_CASUALTY_RATE_FOR_PUSH) {
    score += 1;
    reasons.push(`High casualty rate: ${casualty_rate_per_hour}/hour`);
  }
  
  // Check distance
  if (distance_km >= AUTO_METHOD_THRESHOLDS.DISTANCE_PUSH_PREFERRED_KM) {
    score += 1;
    reasons.push(`Long distance: ${distance_km} km`);
  }
  
  const shouldPush = score >= 2;
  
  return {
    should_push: shouldPush,
    selected_method: shouldPush ? 'PUSH' : 'PULL',
    confidence: score >= 3 ? 'HIGH' : score >= 2 ? 'MEDIUM' : 'LOW',
    score,
    reasons
  };
};

// ============================================================================
// MAIN CALCULATION FUNCTION
// ============================================================================

/**
 * Calculate effective assets based on PUSH/PULL method
 * 
 * @param {Object} facility - Facility with organic_assets
 * @param {string} method - 'PULL', 'PUSH', or 'AUTO'
 * @param {number} distanceToReceiving - Distance to receiving facility in km
 * @param {Object} [conditions] - Optional conditions for AUTO decision
 * 
 * @returns {Object} Effective asset allocation:
 *   - method_used: Actual method applied
 *   - total_assets: Total available ambulances
 *   - collection_assets: Assets allocated to collection
 *   - forward_push_assets: Assets allocated to forward delivery
 *   - collection_capacity_modifier: Modifier applied to collection
 *   - effective_collection_capacity: Patients/hour for collection
 *   - effective_forward_capacity: Patients/hour for forward delivery
 *   - warnings: Any capacity warnings
 */
export const calculateMethodBasedAssets = (facility, method, distanceToReceiving, conditions = {}) => {
  // Validate inputs
  if (!facility) {
    console.error('[MethodBasedAssets] No facility provided');
    return createEmptyResult();
  }
  
  // Get total ground ambulances
  const totalGround = getTotalGroundAmbulances(facility.organic_assets);
  
  if (totalGround === 0) {
    return {
      method_used: method,
      total_assets: 0,
      collection_assets: 0,
      forward_push_assets: 0,
      collection_capacity_modifier: 1.0,
      effective_collection_capacity: 0,
      effective_forward_capacity: 0,
      warnings: ['No ground ambulances available at this facility'],
      time_modifiers: TIME_IMPACT.PULL
    };
  }
  
  // MAM-08: No silent default — missing distance must be configured (APP_RULES 6.3)
  if (distanceToReceiving === undefined || distanceToReceiving === null || distanceToReceiving <= 0) {
    throw new Error(
      '[MethodBasedAssets] calculateMethodBasedAssets: distanceToReceiving not configured. ' +
      'Set facility.evacuation.distance_km in scenario medical configuration.'
    );
  }
  const distance = distanceToReceiving;
  let methodUsed = method;
  let autoDecision = null;
  
  // Handle AUTO method
  if (method === 'AUTO') {
    autoDecision = shouldAutoPush({
      ...conditions,
      distance_km: distance
    });
    methodUsed = autoDecision.selected_method;
  }
  
  // Calculate allocation based on method
  let collectionAssets, forwardAssets, collectionModifier;
  const warnings = [];
  
  if (methodUsed === 'PULL') {
    // PULL: 100% collection, 0% forward
    collectionAssets = totalGround;
    forwardAssets = 0;
    collectionModifier = EVACUATION_METHODS.PULL.collection_modifier;
  } else {
    // PUSH: Split based on distance
    const pushRatio = getPushRatioByDistance(distance);
    
    collectionAssets = Math.round(totalGround * pushRatio.collection_ratio);
    forwardAssets = totalGround - collectionAssets;
    collectionModifier = pushRatio.collection_ratio;
    
    // Add warnings for reduced capacity
    if (collectionModifier < 0.5) {
      warnings.push(`Collection capacity reduced to ${Math.round(collectionModifier * 100)}% due to PUSH method`);
    }
    
    if (collectionAssets < 2) {
      warnings.push('CRITICAL: Less than 2 ambulances for collection - consider requesting additional assets');
    }
  }
  
  // Calculate effective capacities
  const effectiveCollectionCapacity = calculateEvacuationCapacity(
    collectionAssets, 
    distance * 0.5  // Collection typically shorter distance
  );
  
  const effectiveForwardCapacity = calculateEvacuationCapacity(
    forwardAssets,
    distance
  );
  
  // Get time modifiers
  const timeModifiers = TIME_IMPACT[methodUsed] || TIME_IMPACT.PULL;
  
  return {
    method_used: methodUsed,
    method_requested: method,
    auto_decision: autoDecision,
    total_assets: totalGround,
    collection_assets: collectionAssets,
    forward_push_assets: forwardAssets,
    collection_capacity_modifier: collectionModifier,
    forward_capacity_modifier: 1 - collectionModifier,
    effective_collection_capacity: effectiveCollectionCapacity,
    effective_forward_capacity: effectiveForwardCapacity,
    total_effective_capacity: effectiveCollectionCapacity + effectiveForwardCapacity,
    distance_km: distance,
    push_ratio_category: getPushRatioByDistance(distance),
    time_modifiers: timeModifiers,
    warnings,
    
    // Summary for UI display
    summary: {
      collection: `${collectionAssets} ambulances (${Math.round(collectionModifier * 100)}%)`,
      forward: `${forwardAssets} ambulances (${Math.round((1 - collectionModifier) * 100)}%)`,
      capacity: `${effectiveCollectionCapacity} pts/hr collection, ${effectiveForwardCapacity} pts/hr forward`
    }
  };
};

/**
 * Create empty result structure
 */
const createEmptyResult = () => ({
  method_used: 'PULL',
  method_requested: null,
  auto_decision: null,
  total_assets: 0,
  collection_assets: 0,
  forward_push_assets: 0,
  collection_capacity_modifier: 1.0,
  forward_capacity_modifier: 0,
  effective_collection_capacity: 0,
  effective_forward_capacity: 0,
  total_effective_capacity: 0,
  distance_km: 0,
  push_ratio_category: null,
  time_modifiers: TIME_IMPACT.PULL,
  warnings: ['Invalid input'],
  summary: null
});

// ============================================================================
// BATCH CALCULATION FOR MULTIPLE FACILITIES
// ============================================================================

/**
 * Calculate asset allocation for all facilities in an echelon
 * 
 * @param {Array} facilities - Array of facilities
 * @param {string} defaultMethod - Default method if not specified
 * @returns {Array} Array of asset allocation results
 */
export const calculateAllFacilityAssets = (facilities, defaultMethod = 'PULL') => {
  if (!facilities || !Array.isArray(facilities)) {
    return [];
  }
  
  return facilities.map(facility => {
    const method = facility.evacuation_config?.method || 
                   facility.evacuation?.method || 
                   defaultMethod;
    
    // MAM-08: No silent default — missing distance must be configured (APP_RULES 6.3)
    const distance = facility.evacuation?.distance_km ||
                     facility.distance_to_receiving;
    if (!distance) {
      throw new Error(
        `[MethodBasedAssets] calculateAllFacilityAssets: facility "${facility.name || facility.id}" ` +
        'has no evacuation distance configured. Set facility.evacuation.distance_km.'
      );
    }
    
    return {
      facility_id: facility.id,
      facility_name: facility.name,
      facility_type: facility.type,
      ...calculateMethodBasedAssets(facility, method, distance)
    };
  });
};

/**
 * Get aggregate capacity summary for an echelon
 * 
 * @param {Array} assetResults - Results from calculateAllFacilityAssets
 * @returns {Object} Aggregate summary
 */
export const getEchelonCapacitySummary = (assetResults) => {
  if (!assetResults || assetResults.length === 0) {
    return {
      total_facilities: 0,
      total_ambulances: 0,
      total_collection_capacity: 0,
      total_forward_capacity: 0,
      facilities_using_push: 0,
      facilities_with_warnings: 0,
      critical_warnings: []
    };
  }
  
  const pushFacilities = assetResults.filter(r => r.method_used === 'PUSH');
  const withWarnings = assetResults.filter(r => r.warnings?.length > 0);
  const criticalWarnings = assetResults
    .filter(r => r.warnings?.some(w => w.includes('CRITICAL')))
    .map(r => ({ facility: r.facility_name, warnings: r.warnings }));
  
  return {
    total_facilities: assetResults.length,
    total_ambulances: assetResults.reduce((sum, r) => sum + r.total_assets, 0),
    total_collection_capacity: assetResults.reduce((sum, r) => sum + r.effective_collection_capacity, 0),
    total_forward_capacity: assetResults.reduce((sum, r) => sum + r.effective_forward_capacity, 0),
    facilities_using_push: pushFacilities.length,
    facilities_with_warnings: withWarnings.length,
    critical_warnings: criticalWarnings,
    
    // Breakdown by method
    by_method: {
      PULL: assetResults.filter(r => r.method_used === 'PULL').length,
      PUSH: assetResults.filter(r => r.method_used === 'PUSH').length,
      AUTO_DECIDED_PUSH: assetResults.filter(r => 
        r.method_requested === 'AUTO' && r.method_used === 'PUSH'
      ).length
    }
  };
};

// ============================================================================
// IMPACT CALCULATION
// ============================================================================

/**
 * Calculate DOW impact from collection delays due to PUSH method
 * 
 * @param {Object} assetAllocation - Result from calculateMethodBasedAssets
 * @param {number} expectedCasualties - Expected casualties per hour
 * @returns {Object} DOW impact analysis
 */
export const calculatePushDOWImpact = (assetAllocation, expectedCasualties = 5) => {
  const collectionCapacity = assetAllocation.effective_collection_capacity || 0;
  
  // If collection capacity < casualty rate, queue builds
  if (collectionCapacity >= expectedCasualties) {
    return {
      queue_builds: false,
      average_delay_min: 0,
      dow_modifier: 1.0,
      impact: 'NONE',
      description: 'Collection capacity adequate'
    };
  }
  
  // Calculate queue buildup
  const shortfall = expectedCasualties - collectionCapacity;
  const averageDelayMin = (shortfall / collectionCapacity) * 30; // Rough estimate
  
  // DOW increases with delay
  let dowModifier = 1.0;
  let impact = 'LOW';
  
  if (averageDelayMin > 30) {
    dowModifier = 1.15;
    impact = 'MODERATE';
  }
  if (averageDelayMin > 60) {
    dowModifier = 1.35;
    impact = 'HIGH';
  }
  if (averageDelayMin > 90) {
    dowModifier = 1.60;
    impact = 'CRITICAL';
  }
  
  return {
    queue_builds: true,
    shortfall_per_hour: shortfall,
    average_delay_min: Math.round(averageDelayMin),
    dow_modifier: dowModifier,
    impact,
    description: `Collection shortfall of ${shortfall}/hr causes ${Math.round(averageDelayMin)} min average delay`,
    recommendation: assetAllocation.method_used === 'PUSH' 
      ? 'Consider reverting to PULL if receiving facility can handle load'
      : 'Request additional ambulances or MEDEVAC support'
  };
};

// ============================================================================
// MODULE EXPORTS
// ============================================================================

const MethodBasedAssetsModule = {
  calculateMethodBasedAssets,
  calculateAllFacilityAssets,
  getEchelonCapacitySummary,
  calculatePushDOWImpact,
  shouldAutoPush,
  getPushRatioByDistance,
  getTotalGroundAmbulances,
  calculateEvacuationCapacity,
  EVACUATION_METHODS,
  AUTO_METHOD_THRESHOLDS,    // replaces deleted AUTO_THRESHOLDS (MAM-03)
  PUSH_DISTANCE_RATIOS,      // replaces deleted PUSH_DISTANCE_FACTORS (MAM-04)
  ASSET_TYPES,
  TIME_IMPACT
};

export default MethodBasedAssetsModule;
